import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  signIn,
  completeNewPassword,
  forgotPassword,
  confirmPassword,
  AuthTokens,
  CognitoAuthUser,
  getUserFromSession,
} from '../services/cognitoAuth';
import revmacLogo from '../assets/images/revmac-logo.svg';

type AuthStage = 'signIn' | 'newPasswordRequired' | 'forgotPassword' | 'confirmReset';

interface LoginFormProps {
  onAuthenticated: (user: CognitoAuthUser, tokens: AuthTokens) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onAuthenticated }) => {
  const [stage, setStage] = useState<AuthStage>('signIn');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const tokens = await signIn(username, password);
      const user = getUserFromSession(tokens);
      onAuthenticated(user, tokens);
    } catch (err: unknown) {
      const error = err as Error;
      if (error.message === 'NEW_PASSWORD_REQUIRED') {
        setStage('newPasswordRequired');
        setError(null);
      } else if (error.message?.includes('Incorrect username or password')) {
        setError('Incorrect username or password.');
      } else if (error.message?.includes('User does not exist')) {
        setError('User does not exist.');
      } else {
        setError(error.message || 'Authentication failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const tokens = await completeNewPassword(username, password, newPassword);
      const user = getUserFromSession(tokens);
      onAuthenticated(user, tokens);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await forgotPassword(username);
      setInfo('A verification code has been sent to your email.');
      setStage('confirmReset');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to initiate password reset.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await confirmPassword(username, confirmCode, newPassword);
      setInfo('Password reset successfully. Please sign in.');
      setStage('signIn');
      setPassword('');
      setNewPassword('');
      setConfirmCode('');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 420,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem 0',
            background: 'linear-gradient(135deg, #152B52 0%, #2F60B8 50%, #5981C8 100%)',
            color: 'white',
          }}
        >
          <img
            src={revmacLogo}
            alt="RevMAC Logo"
            style={{ height: '48px', marginBottom: '1rem' }}
          />
          <Typography variant="h5" fontWeight={700}>
            RevMAC Credentials
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Sign in to access the credentialing portal
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {info}
            </Alert>
          )}

          {stage === 'signIn' && (
            <form onSubmit={handleSignIn}>
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 1,
                  py: 1.2,
                  backgroundColor: '#152B52',
                  '&:hover': { backgroundColor: '#2395F1' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setStage('forgotPassword');
                  setError(null);
                  setInfo(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Forgot password?
              </Button>
            </form>
          )}

          {stage === 'newPasswordRequired' && (
            <form onSubmit={handleNewPassword}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You must set a new password before continuing.
              </Typography>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                autoComplete="new-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  backgroundColor: '#152B52',
                  '&:hover': { backgroundColor: '#2395F1' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Password'}
              </Button>
            </form>
          )}

          {stage === 'forgotPassword' && (
            <form onSubmit={handleForgotPassword}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your username to receive a password reset code.
              </Typography>
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 1,
                  backgroundColor: '#152B52',
                  '&:hover': { backgroundColor: '#2395F1' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Code'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setStage('signIn');
                  setError(null);
                  setInfo(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Back to sign in
              </Button>
            </form>
          )}

          {stage === 'confirmReset' && (
            <form onSubmit={handleConfirmReset}>
              <TextField
                label="Verification Code"
                fullWidth
                margin="normal"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                required
                autoFocus
              />
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 1,
                  backgroundColor: '#152B52',
                  '&:hover': { backgroundColor: '#2395F1' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setStage('signIn');
                  setError(null);
                  setInfo(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Back to sign in
              </Button>
            </form>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginForm;
